// #3570 (show-setup workstream) -- what each of the 7 Setup checklist items
// says on the Pre-show dashboard. Every string is a STATIC i18n key (one `t()`
// call per key, never a key built from a variable), so the lookups below are
// switch statements. The server (#3569, apps/score-api/src/lib/show-setup.js)
// sends a reason code and the ids a Fix link needs on every row; this file
// turns them into words and a link to the exact page (docs/adr/0004).

import { readServerFailure } from "common-fe";

// score-api's SETUP_ITEM_KEYS, in the order the dashboard lists them.
export const SETUP_ITEM_ORDER = [
  "scribes",
  "divisionSettings",
  "judgeSignatures",
  "compositeJudges",
  "classTests",
  "federationIds",
  "showPin",
  "reviewQueue",
];

/** The server's plain-words reason for a failed write (ADR 0004), else our own line. Never "Server Error". */
export function writeErrorText(err, fallback) {
  return readServerFailure(err).message || fallback;
}

/** The hand check hit an item the server had already cleared since the page loaded. */
export function isItemAlreadyDone(err) {
  return readServerFailure(err).code === "show_setup_item_done";
}

// Mirrors App.jsx's SETTINGS_PATH. Not imported: App.jsx imports ShowDashboard,
// and this module is reached from it.
const SETTINGS = "/settings";

/** The Settings page, scrolled to one card (SettingsPage reads the hash). No anchor = the page. */
export function settingsPath(anchor) {
  return anchor ? `${SETTINGS}#${anchor}` : SETTINGS;
}

/** How many things are still wrong in an item (the Review Queue keeps its own count). */
export function openCountOf(item) {
  if (item.key === "reviewQueue") return item.openCount ?? item.openItems?.length ?? 0;
  return item.rows?.length ?? 0;
}

/** The rows the Details drawer lists. */
export function detailRowsOf(item) {
  return item.key === "reviewQueue" ? (item.openItems ?? []) : (item.rows ?? []);
}

/**
 * Where the Fix link for ONE row goes. null = there is no in-app page for it (a rider, owner,
 * trainer or horse id lives in FVD), so the caller shows plain "Fix in FVD" text, not a link.
 */
export function rowFixPath(itemKey, row) {
  switch (itemKey) {
    case "federationIds":
      // #3585: a judge's id is edited in the Judges drawer; everyone else's comes from FVD.
      if (row?.fixIn === "judges") {
        return row.judgeId ? `/judges?judge=${encodeURIComponent(row.judgeId)}` : "/judges";
      }
      return null;
    case "scribes":
    case "divisionSettings":
    case "classTests":
      return row?.classId ? `/classes/${row.classId}` : "/classes";
    case "judgeSignatures": {
      // A composite roster row is signed through its real judges: open the first one still unsigned.
      const judgeId = row?.missingJudgeIds?.[0] ?? row?.judgeId;
      return judgeId ? `/judges?judge=${encodeURIComponent(judgeId)}` : "/judges";
    }
    case "compositeJudges":
      // The mapping lives on the Composite judges tab, not in a judge's drawer.
      return "/judges?tab=composite";
    case "showPin":
      return settingsPath("scribe-pin-card");
    default:
      return row?.itemId != null ? `/review-queue?item=${encodeURIComponent(row.itemId)}` : "/review-queue";
  }
}

/** Where the card's Fix link goes: the exact row when only one is wrong, else the page. */
export function itemFixPath(item) {
  const rows = detailRowsOf(item);
  if (rows.length === 1) return rowFixPath(item.key, rows[0]);
  switch (item.key) {
    case "federationIds":
      // Only an all-judges list has one page to send you to; anything with an FVD row is fixed in FVD.
      return rows.length > 0 && rows.every((r) => r.fixIn === "judges") ? "/judges" : null;
    case "scribes":
    case "divisionSettings":
    case "classTests":
      return "/classes";
    case "judgeSignatures":
      return "/judges";
    case "compositeJudges":
      return "/judges?tab=composite";
    case "showPin":
      return settingsPath("scribe-pin-card");
    default:
      return "/review-queue";
  }
}

export function itemTitle(t, key) {
  switch (key) {
    case "scribes":
      return t("dashboard.setup.items.scribes.title");
    case "divisionSettings":
      return t("dashboard.setup.items.divisionSettings.title");
    case "judgeSignatures":
      return t("dashboard.setup.items.judgeSignatures.title");
    case "compositeJudges":
      return t("dashboard.setup.items.compositeJudges.title");
    case "classTests":
      return t("dashboard.setup.items.classTests.title");
    case "federationIds":
      return t("dashboard.setup.items.federationIds.title");
    case "showPin":
      return t("dashboard.setup.items.showPin.title");
    default:
      return t("dashboard.setup.items.reviewQueue.title");
  }
}

/** What it means to mark an open item done anyway (the hand-check confirm). */
export function itemConfirmCopy(t, key) {
  switch (key) {
    case "scribes":
      return t("dashboard.setup.items.scribes.confirm");
    case "divisionSettings":
      return t("dashboard.setup.items.divisionSettings.confirm");
    case "judgeSignatures":
      return t("dashboard.setup.items.judgeSignatures.confirm");
    case "compositeJudges":
      return t("dashboard.setup.items.compositeJudges.confirm");
    case "classTests":
      return t("dashboard.setup.items.classTests.confirm");
    case "federationIds":
      return t("dashboard.setup.items.federationIds.confirm");
    case "showPin":
      return t("dashboard.setup.items.showPin.confirm");
    default:
      return t("dashboard.setup.items.reviewQueue.confirm");
  }
}

/** The one-line status under an item's title. */
export function itemStatus(t, item) {
  const count = openCountOf(item);
  const total = item.total ?? 0;
  if (item.handChecked) return t("dashboard.setup.status.stillOpen", { count });
  if (item.done) {
    switch (item.key) {
      case "showPin":
        return t("dashboard.setup.items.showPin.done");
      case "reviewQueue":
        return t("dashboard.setup.items.reviewQueue.done");
      default:
        return t("dashboard.setup.status.allSet");
    }
  }
  switch (item.key) {
    case "scribes":
      return t("dashboard.setup.items.scribes.status", { count, total });
    case "divisionSettings":
      return t("dashboard.setup.items.divisionSettings.status", { count, total });
    case "judgeSignatures":
      return t("dashboard.setup.items.judgeSignatures.status", { count, total });
    case "compositeJudges":
      return t("dashboard.setup.items.compositeJudges.status", { count, total });
    case "classTests":
      return t("dashboard.setup.items.classTests.status", { count, total });
    case "federationIds":
      return t("dashboard.setup.items.federationIds.status", { count, total });
    case "showPin":
      return t("dashboard.setup.items.showPin.status");
    default:
      return t("dashboard.setup.items.reviewQueue.status", { count });
  }
}

/** The word on the card's Fix link, where it is not just "Fix". */
export function itemFixLabel(t, key) {
  return key === "reviewQueue" ? t("dashboard.setup.openReviewQueue") : t("dashboard.setup.fix");
}

function classLabel(t, row) {
  return row.classNumber != null && row.classNumber !== ""
    ? t("dashboard.setup.classLabel", { number: row.classNumber })
    : t("dashboard.setup.classUnnumbered");
}

const dash = "—";

// Review Queue issue types, as plain words. Static keys, same table as ReviewQueue.jsx.
function issueTypeLabel(t, issueType) {
  switch (issueType) {
    case "manual_ride":
      return t("reviewQueue.issueTypes.manual_ride");
    case "fvd_writeback_failure":
      return t("reviewQueue.issueTypes.fvd_writeback_failure");
    case "quarantined_payload":
      return t("reviewQueue.issueTypes.quarantined_payload");
    case "unsigned_sheet":
      return t("reviewQueue.issueTypes.unsigned_sheet");
    case "contender_pick":
      return t("reviewQueue.issueTypes.contender_pick");
    case "station_reconcile":
      return t("reviewQueue.issueTypes.station_reconcile");
    case "stale_write_conflict":
      return t("reviewQueue.issueTypes.stale_write_conflict");
    case "scribe_review_request":
      return t("reviewQueue.issueTypes.scribe_review_request");
    case "totals_disagreement":
      return t("reviewQueue.issueTypes.totals_disagreement");
    case "fvd_orphan":
      return t("reviewQueue.issueTypes.fvd_orphan");
    case "orphan_sheet":
      return t("reviewQueue.issueTypes.orphan_sheet");
    default:
      return t("reviewQueue.issueTypes.other");
  }
}

// #3585 -- static keys only, one t() per key.
function federationRoleLabel(t, role) {
  switch (role) {
    case "rider":
      return t("dashboard.setup.items.federationIds.roles.rider");
    case "owner":
      return t("dashboard.setup.items.federationIds.roles.owner");
    case "trainer":
      return t("dashboard.setup.items.federationIds.roles.trainer");
    case "horse":
      return t("dashboard.setup.items.federationIds.roles.horse");
    default:
      return t("dashboard.setup.items.federationIds.roles.judge");
  }
}

/**
 * #3603: a rider, owner or trainer with no USEF id may ride on a Show Pass (USEF Results Criteria
 * item 5.b), which staff declare per class at Publish Results. A horse or a judge cannot.
 */
export function canDeclareShowPass(row) {
  return row?.fixIn !== "judges" && (row?.role === "rider" || row?.role === "owner" || row?.role === "trainer");
}

/** What is missing, and where to add it (ADR 0004: what, why, where to fix). */
function federationIdProblem(t, row) {
  const usef = row.missing?.includes("usef");
  const usdf = row.missing?.includes("usdf");
  const what =
    usef && usdf
      ? t("dashboard.setup.items.federationIds.missing.both")
      : usdf
        ? t("dashboard.setup.items.federationIds.missing.usdf")
        : t("dashboard.setup.items.federationIds.missing.usef");
  if (row.fixIn === "judges") return t("dashboard.setup.reasons.federation_id_missing_judges", { what });
  // #3603: a rider, owner or trainer can instead be declared Show Pass (EX) at Publish Results.
  if (canDeclareShowPass(row)) return t("dashboard.setup.reasons.federation_id_missing_showpass", { what });
  return t("dashboard.setup.reasons.federation_id_missing_fvd", { what });
}

/** Column headings of the Details table. */
export function detailColumns(t, key) {
  switch (key) {
    case "scribes":
      return [
        t("dashboard.setup.columns.class"),
        t("dashboard.setup.columns.ring"),
        t("dashboard.setup.columns.position"),
        t("dashboard.setup.columns.problem"),
      ];
    case "divisionSettings":
    case "classTests":
      return [
        t("dashboard.setup.columns.class"),
        t("dashboard.setup.columns.ring"),
        t("dashboard.setup.columns.problem"),
      ];
    case "judgeSignatures":
      return [t("dashboard.setup.columns.judge"), t("dashboard.setup.columns.problem")];
    case "compositeJudges":
      return [t("dashboard.setup.columns.judgeEntry"), t("dashboard.setup.columns.problem")];
    case "federationIds":
      return [
        t("dashboard.setup.columns.who"),
        t("dashboard.setup.columns.cno"),
        t("dashboard.setup.columns.classes"),
        t("dashboard.setup.columns.problem"),
      ];
    case "showPin":
      return [t("dashboard.setup.columns.setting"), t("dashboard.setup.columns.problem")];
    default:
      return [t("dashboard.setup.columns.held"), t("dashboard.setup.columns.problem")];
  }
}

/** One Details row as display cells, in the order of detailColumns. */
export function detailCells(t, key, row) {
  switch (key) {
    case "scribes":
      return [
        classLabel(t, row),
        row.ring ?? dash,
        row.position ?? dash,
        t("dashboard.setup.reasons.scribe_missing"),
      ];
    case "divisionSettings":
      return [classLabel(t, row), row.ring ?? dash, t("dashboard.setup.reasons.division_settings_missing")];
    case "classTests":
      return [classLabel(t, row), row.ring ?? dash, t("dashboard.setup.reasons.class_test_missing")];
    case "judgeSignatures":
      return [
        row.judgeName || dash,
        row.composite
          ? t("dashboard.setup.reasons.signature_missing_composite")
          : t("dashboard.setup.reasons.signature_missing"),
      ];
    case "compositeJudges":
      return [row.judgeName || dash, t("dashboard.setup.reasons.composite_not_mapped")];
    case "federationIds":
      return [
        `${federationRoleLabel(t, row.role)}: ${row.name || dash}`,
        row.cnos?.length ? row.cnos.join(", ") : dash,
        row.classNumbers?.length ? row.classNumbers.filter((n) => n != null).join(", ") || dash : dash,
        federationIdProblem(t, row),
      ];
    case "showPin":
      return [t("dashboard.setup.reasons.show_pin_setting"), t("dashboard.setup.reasons.show_pin_missing")];
    default:
      return [issueTypeLabel(t, row.issueType), t("dashboard.setup.reasons.review_item_open")];
  }
}
